import React, { useState, useEffect } from 'react';
import { KpiCard, toneColor } from '@/design-system/dna';
import api from '@/lib/api';
import { FaltantesAprovacaoDialog, useFaltantes } from '@/components/notas/FaltantesAprovacao';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  Users, BookOpen, GraduationCap, TrendingUp, TrendingDown,
  AlertTriangle, CheckCircle, Award, Target, BarChart3,
  ArrowUp, ArrowDown, Clock, RefreshCw, Sparkles
} from 'lucide-react';
import {
  BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LabelList
} from 'recharts';

// Valor com 1 casa decimal (pt-BR). Nunca converte ausência de nota em 0.
const fmt1 = (v) => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)))
  ? null : Number(v).toFixed(1).replace('.', ',');

// Cores premium
const COLORS = {
  purple: '#8E52AB',
  green: '#10B981',
  blue: '#3B82F6',
  orange: '#F59E0B',
  red: '#EF4444',
  pink: '#EC4899',
  cyan: '#06B6D4'
};

const STATUS_COLORS = {
  excelente: '#1FA34A',
  satisfatorio: '#7B3F98',
  critico: '#E2412B'
};

// Componente KPI Card Premium
function KPICard({ title, value, subtitle, icon, color, trendLabel, onClick }) {
  return <KpiCard id={`notas-${String(title).toLowerCase().replace(/\s+/g, '-')}`} icon={icon} label={title} value={value} hint={[subtitle, trendLabel].filter(Boolean).join(' · ')} color={toneColor(color)} onClick={onClick} />;
}

// Card de Destaque (Melhor/Pior)
function DestaqueCard({ title, nome, valor, icon: Icon, tipo }) {
  const isMelhor = tipo === 'melhor';
  
  return (
    <Card className={`bg-white border ${isMelhor ? 'border-green-200' : 'border-orange-200'} shadow-sm`}>
      <CardContent className="py-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${isMelhor ? 'bg-green-100' : 'bg-orange-100'}`}>
            <Icon className={`w-5 h-5 ${isMelhor ? 'text-green-600' : 'text-orange-600'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-slate-500">{title}</p>
            <p className="text-sm font-semibold text-slate-800 truncate">{nome || 'N/A'}</p>
          </div>
          <div className={`px-3 py-1 rounded-lg ${isMelhor ? 'bg-green-500' : 'bg-orange-500'} text-white font-bold`}>
            {valor?.toFixed(1) || '0.0'}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function VisaoGlobal({ year, trimestre, turmaId }) {
  const [showFaltantes, setShowFaltantes] = useState(false);
  const faltantes = useFaltantes('/central-notas/faltantes-aprovacao', { year: year || new Date().getFullYear() });
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  
  useEffect(() => {
    loadData();
  }, [year, trimestre, turmaId]);
  
  const loadData = async () => {
    setLoading(true);
    try {
      const params = { year };
      if (trimestre) params.trimestre = trimestre;
      if (turmaId) params.turma_id = turmaId;
      
      const res = await api.get('/central-notas/visao-global', { params });
      setData(res.data);
    } catch (error) {
      console.error('Erro ao carregar visão global:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {[1, 2, 3, 4, 5].map(i => (
            <Skeleton key={i} className="h-28 bg-slate-100" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-80 bg-slate-100" />
          <Skeleton className="h-80 bg-slate-100" />
        </div>
      </div>
    );
  }
  
  const kpis = data?.kpis || {};
  
  // Dados para gráfico de trimestres
  // Somente trimestres com média REAL (null/sem fechamento não gera ponto nem número)
  const trimestresValidos = (data?.trimestres || []).filter(
    t => t.media !== null && t.media !== undefined && t.media !== '' && !Number.isNaN(Number(t.media))
  );
  const trimestresChart = trimestresValidos.map((t, i) => {
    const media = Number(t.media);
    const anterior = i > 0 ? Number(trimestresValidos[i - 1].media) : null;
    return {
      name: t.periodo?.replace(' Trimestre', 'º Tri') || t.periodo,
      media,
      total: t.total,
      delta: anterior === null ? null : Math.round((media - anterior) * 10) / 10,
    };
  });
  
  // Dados para gráfico de tipos
  const tiposChart = (data?.distribuicao_tipos || []).filter(t => t.tipo && !t.tipo.toLowerCase().includes('bimestral')).map(t => ({
    name: t.tipo,
    value: t.total,
    media: t.media
  }));
  
  // Distribuição por status (estimado)
  const totalNotas = kpis.total_notas || 1;
  const distribuicaoStatus = [
    { name: 'Excelentes (>=8)', value: kpis.notas_excelentes || 0, color: STATUS_COLORS.excelente },
    { name: 'Satisfatorias (6-7.9)', value: totalNotas - (kpis.notas_excelentes || 0) - (kpis.notas_criticas || 0), color: STATUS_COLORS.satisfatorio },
    { name: 'Criticas (<6)', value: kpis.notas_criticas || 0, color: STATUS_COLORS.critico }
  ].filter(d => d.value > 0);
  
  return (
    <div className="space-y-6" data-testid="visao-global">
      {/* KPIs Principais */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <KPICard
          title="Média Geral"
          value={kpis.media_geral?.toFixed(1) || '0.0'}
          subtitle="da escola"
          icon={Target}
          color="purple"
          highlight
        />
        <KPICard
          title="Total de Alunos"
          value={kpis.total_alunos || 0}
          subtitle={`${kpis.total_turmas || 0} turmas`}
          icon={Users}
          color="blue"
        />
        <KPICard
          title="Notas Lançadas"
          value={kpis.total_notas || 0}
          subtitle={`${kpis.total_disciplinas || 0} disciplinas`}
          icon={BookOpen}
          color="cyan"
        />
        <KPICard
          title="Em Recuperação"
          value={kpis.alunos_em_recuperacao || 0}
          subtitle={`${kpis.total_recuperacoes || 0} notas`}
          icon={AlertTriangle}
          color="orange"
        />
        <KPICard
          title="Notas Críticas"
          value={kpis.notas_criticas || 0}
          subtitle="abaixo de 5"
          icon={TrendingDown}
          color="red"
        />
        <KPICard
          title="Notas faltantes p/ aprovação"
          value={faltantes.data ? (faltantes.data.summary.vermelho + faltantes.data.summary.laranja) : '…'}
          subtitle={faltantes.data ? `em risco · ${faltantes.data.summary.vermelho} acima de 10 · ver todos` : 'calculando…'}
          icon={Target}
          color="orange"
          onClick={() => setShowFaltantes(true)}
        />
      </div>
      <FaltantesAprovacaoDialog open={showFaltantes} onOpenChange={setShowFaltantes} scope="central" year={year || new Date().getFullYear()} defaultClassId={turmaId || undefined} />
      
      {/* Destaques */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <DestaqueCard
          title="Melhor Disciplina"
          nome={data?.melhor_disciplina?.nome}
          valor={data?.melhor_disciplina?.media}
          icon={Award}
          tipo="melhor"
        />
        <DestaqueCard
          title="Disciplina em Alerta"
          nome={data?.pior_disciplina?.nome}
          valor={data?.pior_disciplina?.media}
          icon={AlertTriangle}
          tipo="alerta"
        />
        <DestaqueCard
          title="Melhor Turma"
          nome={data?.melhor_turma?.nome}
          valor={data?.melhor_turma?.media}
          icon={GraduationCap}
          tipo="melhor"
        />
        <DestaqueCard
          title="Turma em Alerta"
          nome={data?.pior_turma?.nome}
          valor={data?.pior_turma?.media}
          icon={Users}
          tipo="alerta"
        />
      </div>
      
      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Média por Trimestre */}
        <Card className="bg-white border border-purple-200/50 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-slate-800 flex items-center gap-2 text-base">
              <TrendingUp className="w-5 h-5 text-purple-500" />
              Média por Trimestre
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {trimestresChart.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trimestresChart} margin={{ top: 34, right: 28, left: 4, bottom: 4 }}>
                    <defs>
                      <linearGradient id="colorMedia" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.purple} stopOpacity={0.3}/>
                        <stop offset="95%" stopColor={COLORS.purple} stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" stroke="#94A3B8" tick={{ fontSize: 12 }} />
                    <YAxis domain={[0, 10]} stroke="#94A3B8" tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #E2E8F0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                      }}
                      formatter={(value, name) => [value.toFixed(2), name === 'media' ? 'Média' : name]}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="media" 
                      stroke={COLORS.purple} 
                      strokeWidth={3}
                      fillOpacity={1} 
                      fill="url(#colorMedia)"
                      name="Média"
                      dot={{ r: 4, fill: COLORS.purple, strokeWidth: 0 }}
                      connectNulls={false}
                    >
                      <LabelList
                        dataKey="media"
                        position="top"
                        offset={10}
                        formatter={fmt1}
                        style={{ fontSize: 13, fontWeight: 700, fill: '#3F1F5C' }}
                      />
                      <LabelList
                        dataKey="delta"
                        position="top"
                        offset={26}
                        formatter={(d) => (d === null || d === undefined
                          ? null
                          : `${d > 0 ? '↑ +' : d < 0 ? '↓ ' : '→ '}${fmt1(d)}`)}
                        style={{ fontSize: 11, fontWeight: 600, fill: '#64748B' }}
                      />
                    </Area>
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-400">
                  Sem dados de trimestres
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Distribuição por Tipo de Avaliação */}
        <Card className="bg-white border border-purple-200/50 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-slate-800 flex items-center gap-2 text-base">
              <BarChart3 className="w-5 h-5 text-purple-500" />
              Média por Tipo de Avaliação
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {tiposChart.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={tiposChart} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" domain={[0, 10]} stroke="#94A3B8" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" stroke="#94A3B8" tick={{ fontSize: 11 }} width={120} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #E2E8F0',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => [value.toFixed(2), 'Média']}
                    />
                    <Bar dataKey="media" fill={COLORS.purple} radius={[0, 4, 4, 0]}>
                      <LabelList dataKey="media" position="right" formatter={fmt1}
                                 style={{ fontSize: 12, fontWeight: 700, fill: '#3F1F5C' }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-400">
                  Sem dados de tipos
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Distribuição de Notas */}
      <Card className="bg-white border border-purple-200/50 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-slate-800 flex items-center gap-2 text-base">
            <Sparkles className="w-5 h-5 text-purple-500" />
            Distribuição Geral
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Cards de Resumo */}
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-green-50 border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-700 font-medium">Excelentes</p>
                    <p className="text-xs text-green-600">&ge; 8.0</p>
                  </div>
                  <p className="text-3xl font-bold text-green-700">{kpis.notas_excelentes || 0}</p>
                </div>
              </div>
              
              <div className="p-4 rounded-xl bg-orange-50 border border-orange-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-orange-700 font-medium">Satisfatorias</p>
                    <p className="text-xs text-orange-600">6.0 a 7.9</p>
                  </div>
                  <p className="text-3xl font-bold text-orange-700">{kpis.notas_abaixo_media || 0}</p>
                </div>
              </div>
              
              <div className="p-4 rounded-xl bg-red-50 border border-red-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-red-700 font-medium">Criticas</p>
                    <p className="text-xs text-red-600">&lt; 6.0</p>
                  </div>
                  <p className="text-3xl font-bold text-red-700">{kpis.notas_criticas || 0}</p>
                </div>
              </div>
            </div>
            
            {/* Gráfico Pizza */}
            <div className="md:col-span-2 h-64">
              {distribuicaoStatus.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={distribuicaoStatus}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {distribuicaoStatus.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #E2E8F0',
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-400">
                  Sem dados para exibir
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
